import { ApiError } from './core'
import { maybeCaptureApiError } from '../../features/bug-report/captureApiError'
import { buildQueryString } from '../../utils/apiQueryBuilder'
import { config } from '../../config'
import { createServiceFetch } from '../../utils/validateFetchUrl'
import { resolveOAuthRefreshTokenForLongRuns } from '../../utils/oauthRefreshForLongRuns'

/** Resolve at call time so SSRF base stays aligned with `agentEvalsRequest` URL builder. */
function agentEvalsFetch(url: string, init?: RequestInit): Promise<Response> {
  return createServiceFetch(config.api.aiservicesUrl)(url, init)
}

export type CompareOp = '>=' | '>' | '<=' | '<' | '=='

export interface MetricScoringRules {
  direction: 'higher_is_better' | 'lower_is_better'
  green_at: number
  amber_at: number
  red_at: number
  green_op?: CompareOp
  amber_op?: CompareOp
  red_op?: CompareOp
}

export interface MetricConfigEntry {
  metric_key: string
  framework: 'deepeval' | 'ragas' | 'AgentX'
  template_id?: string
  threshold?: number
  scoring_rules?: MetricScoringRules
}

export interface EvalSession {
  id: string
  tenant_id: string
  project_id: string
  subject_type?: 'agent' | 'workflow'
  agent_id: string
  agent_version: string
  agent_status?: string | null
  workflow_id_ref?: string | null
  workflow_version_ref?: string | null
  session_name: string
  status: string
  eval_framework: string
  metrics_selected: string[] | null
  metrics_config: MetricConfigEntry[] | null
  dataset_table_id: string
  row_selection: Record<string, unknown> | null
  progress_pct: number
  rows_completed: number
  rows_total: number
  tags: string[] | null
  created_at: number | null
  started_at: number | null
  completed_at: number | null
  error_summary: string | null
  summary_metrics: Record<string, { value: number; aggregation: string }> | null
  created_by?: string | null
  created_by_name?: string | null
  /** Link to this run in the orchestrator UI (e.g. Temporal Web). */
  orchestrator_run_url?: string | null
}

/** Per-metric eval result from DeepEval/Ragas/AgentX adapters (stored in row metrics JSON). */
export interface EvalMetricScore {
  score: number | null
  threshold?: number | null
  passed?: boolean | null
  rationale?: string | null
  failure_category?: string | null
  raw?: Record<string, unknown> | null
}

export interface EvalRowResult {
  id: string
  session_id: string
  row_index: number
  input: string | null
  expected_output: string | null
  context: string | null
  agent_response: string | null
  /** Legacy rows may use plain numbers; new evals store structured scores with rationale. */
  metrics: Record<string, number | EvalMetricScore> | null
  tokens_prompt: number | null
  tokens_completion: number | null
  tokens_total: number | null
  latency_ms: number | null
  cost_usd: number | null
  row_error_code: string | null
  row_error_message: string | null
  masking_applied: boolean
  traceability?: EvalRowTraceability | null
}

export interface EvalRowTraceability {
  builder_session_id?: string | null
  execution_mode?: string | null
  generated_artifact_id?: string | null
  generated_artifact_type?: 'agent' | 'workflow' | null
}

/** Histogram bin for query score distribution. */
export interface ScoreDistributionBin {
  start: number
  end: number
  count: number
}

/** Response from session metric distribution API: histogram + mean, std_dev, n. */
export interface ScoreDistributionResponse {
  bins: ScoreDistributionBin[]
  mean: number | null
  std_dev: number | null
  n: number
}

export interface AgentSummaryItem {
  agent_id: string
  agent_version: string
  metric_key: string
  metric_value: number
  aggregation: string
  session_id: string
  session_name: string | null
  evaluated_at: number | null
}

/** Summary stats for Agent Evals tab KPI cards. */
export interface AgentEvalsSummary {
  agents_evaluated: number
  total_eval_sessions: number
  active_eval_sessions_count: number
  eval_session_success_rate_pct: number | null
  total_cost_usd: number
  total_tokens: number
  input_tokens: number
  output_tokens: number
}

/** One aggregated evaluation entry per agent (portfolio list from API). */
export interface AgentPortfolioEntry {
  agent_id: string
  agent_name: string | null
  agent_status?: string | null
  agent_version: string
  subject_type?: 'agent' | 'workflow'
  latest_session_id: string
  latest_session_name: string | null
  latest_evaluated_at: number | null
  /** Eval session row status: queued | running | completed | failed | … */
  latest_eval_session_status?: string | null
  metrics: Record<string, number>
}

export interface MetricCatalogEntry {
  key: string
  description: string
  required_fields: string[]
  frameworks: string[]
  requires_context?: boolean
  requires_embedding?: boolean
  requires_sql_connection?: boolean
  requires_schema_definition?: boolean
  /** False for metrics scored without an LLM judge (ROUGE, BLEU, Text2SQL, classification, graph). */
  requires_judge?: boolean
  higher_is_better?: boolean
  recommended_threshold?: number | null
  is_template?: boolean
  template_id?: string
  project_id?: string | null
  category?: string | null
}

export interface MetricCatalogResponse {
  frameworks: string[]
  metrics: MetricCatalogEntry[]
  custom_templates?: MetricCatalogEntry[]
}

export interface MetricGlossaryEntry {
  key: string
  framework: string
  name: string
  summary: string
  definition: string | null
  when_to_use: string | null
  required_fields: string[]
  higher_is_better: boolean
  recommended_threshold: number | null
}

export interface MetricGlossaryResponse {
  metrics: MetricGlossaryEntry[]
}

export interface EvalTemplate {
  id: string
  tenant_id: string
  project_id: string | null
  /** Resolved from TAGP Backend when listing or fetching a template */
  project_name?: string | null
  name: string
  description: string | null
  framework: string
  template_code: string | null
  template_prompt: string | null
  judge_model_config: Record<string, unknown> | null
  metrics_output_schema: Record<string, unknown> | null
  is_global: boolean
  version: number
  created_at: number | null
  updated_at: number | null
  created_by?: string | null
  created_by_name?: string | null
}

/** Eval session config sent to AIServices; supports per-metric pass/fail thresholds. */
export interface EvalConfigPayload {
  foundation_model_id?: string
  embedding_model_id?: string
  context_source?: 'dataset' | 'agent_retrieval' | 'both'
  /** Per-metric score thresholds (0.0–1.0). When set, evaluation returns pass/fail per metric. */
  thresholds?: Record<string, number>
  /** Judge model temperature (0–2). */
  temperature?: number
  /** Judge model max tokens. */
  max_tokens?: number
  /** TAGP database connector for Text2SQL execution metrics (EX, VES). */
  sql_connector_id?: string
  /** Registered storage folder used to fetch files referenced in live OCR evals. */
  storage_folder_id?: string
  /** Advanced SQLAlchemy URL override for Text2SQL execution metrics. */
  sql_connection_string?: string
  /** Declared schema JSON for text2sql_schema_compliance. */
  schema_definition?: Record<string, unknown> | unknown[] | string
  /** Agent evals: 'agent' invokes live; 'direct' scores stored output column. */
  evaluation_run_mode?: 'agent' | 'direct'
  /**
   * ABA builder evals: how many build turns a row gets. '0shot' scores the graph
   * ABA first builds; '1shot' sends the standard graph-review follow-up once more
   * and scores the corrected graph against the same ground truth.
   */
  eval_mode?: '0shot' | '1shot'
  /** Override judge model id. */
  judge_model?: string
  custom_code?: string
  /** Virtual Direct eval dataset from read-only SQL JOIN (t1=workflow output, t2=ground truth). */
  dataset_join?: DatasetJoinSpec
  /** Session turns JOIN benchmark table (t1=sessions, t2=benchmark). */
  session_benchmark_join?: SessionBenchmarkJoinSpec
  /**
   * Registry external agents. AIServices live-invokes these through the Backend
   * external-agent chat adapter instead of Agentserve — see
   * `is_external_agent_eval_subject` / `execute_external_agent_for_benchmark`.
   */
  agent_kind?: 'native' | 'external'
  external_agent_id?: string
  [key: string]: unknown
}

export interface SessionBenchmarkJoinSpec {
  session_ids: string[]
  session_source: 'agentserve' | 'aba' | 'cwb'
  ground_truth_table_id: string
  join_sql: string
  row_limit?: number
}

export interface EvalSessionBenchmarkJoinPreviewResponse {
  sql: string
  columns: Array<Record<string, unknown>>
  preview_rows: Record<string, unknown>[]
  page: number
  page_size: number
  row_count: number
  session_turn_count?: number
}

export interface DatasetJoinSpec {
  workflow_output_table_id: string
  ground_truth_table_id: string
  join_sql: string
  row_limit?: number
}

export interface EvalDatasetJoinPreviewResponse {
  sql: string
  columns: Array<Record<string, unknown>>
  preview_rows: Record<string, unknown>[]
  page: number
  page_size: number
  row_count: number
}

export interface EvalDatasetJoinMaterializeResponse {
  table_id: string
  table_name: string
  row_count: number
  sql: string
  columns: Array<Record<string, unknown>>
}

export interface CreateSessionPayload {
  /** Entity under evaluation. Defaults to 'agent' on the backend. */
  subject_type?: 'agent' | 'workflow'
  /** Required when subject_type is 'agent' (or omitted). */
  agent_id?: string
  agent_version?: string
  /** Required when subject_type is 'workflow'. */
  workflow_id?: string
  workflow_version?: string
  session_name: string
  eval_framework?: string
  metrics_selected?: string[]
  metrics_config?: MetricConfigEntry[]
  dataset_table_id: string
  column_mapping: {
    input_column: string
    expected_output_column?: string
    context_column?: string
    context_columns?: string[]
    metadata_column?: string
    /** External knowledge / hint sent to the agent with the question (BIRD "evidence"). */
    evidence_column?: string
    /** Column holding pre-materialized output (workflow evals and direct agent evals). */
    response_column?: string
    /** Optional column of storage object keys / relative paths for OCR file evals. */
    file_path_column?: string
  }
  row_selection?: {
    mode: string
    start?: number
    end?: number
    sample_size?: number
    seed?: number
    /** Absolute 0-based dataset indices when mode is "custom". */
    indices?: number[]
  }
  custom_template_id?: string
  eval_config?: EvalConfigPayload
  tags?: string[]
  /** Azure AD: enables AIServices to renew access tokens during long eval runs. */
  oauth_refresh_token?: string | null
}

/** One workflow final-output table (resolved to a registered-table id). */
export interface WorkflowOutputTable {
  id: string
  display_name: string | null
  table?: string | null
  database?: string | null
  schema?: string | null
  row_count?: number | null
  columns: Array<Record<string, unknown>>
  node_id?: string | null
  node_label?: string | null
}

export interface WorkflowOutputTablesResponse {
  workflow_id: string
  workflow_name: string | null
  project_id: string | null
  version_id: string | null
  items: WorkflowOutputTable[]
}

export interface CreateTemplatePayload {
  name: string
  description?: string
  framework?: string
  template_code?: string
  template_prompt?: string
  judge_model_config?: Record<string, unknown>
  metrics_output_schema?: Record<string, unknown>
  is_global?: boolean
  project_id?: string | null
}

export interface BatchCreateTemplatesPayload {
  templates: CreateTemplatePayload[]
}

export type ImportConflictPolicy = 'skip' | 'update'

export interface PortableEvalTemplate {
  name: string
  description?: string | null
  framework: string
  template_code?: string | null
  template_prompt?: string | null
  judge_model_config?: Record<string, unknown> | null
  metrics_output_schema?: Record<string, unknown> | null
  is_global?: boolean
}

export interface ExportTemplatesResponse {
  format: string
  templates: PortableEvalTemplate[]
}

export interface ImportTemplatesPayload {
  content: string
  project_id?: string | null
  is_global?: boolean
  on_conflict?: ImportConflictPolicy
}

export interface ImportTemplatesResponse {
  created: number
  updated: number
  skipped: number
  errors: string[]
  templates: EvalTemplate[]
}

export interface UpdateTemplatePayload {
  name?: string
  description?: string
  template_code?: string
  template_prompt?: string | null
  judge_model_config?: Record<string, unknown>
  metrics_output_schema?: Record<string, unknown>
  is_global?: boolean
  project_id?: string | null
}

export interface GenerateTemplatePayload {
  business_context: string
  framework: 'deepeval' | 'ragas' | 'AgentX' | 'RACE'
  foundation_model_id: string
}

export interface GenerateTemplateResponse {
  name: string
  description: string
  framework: string
  template_prompt: string | null
  metrics_output_schema: Record<string, unknown>
}

export interface BulkTemplateDeleteResponse {
  deleted: number
  skipped: number
  errors: string[]
}

export interface LeaderboardMetricColumn {
  key: string
  label: string
  framework: string | null
  higher_is_better: boolean
}

export interface LeaderboardEntry {
  rank: number
  subject_id: string
  subject_name: string | null
  subject_type: 'agent' | 'workflow' | string
  version: string
  project_id: string | null
  overall: number
  metrics: Record<string, number>
  session_id: string
  session_name: string | null
  evaluated_at: number | null
  rows_total: number
  run_count: number
}

export interface LeaderboardResponse {
  dataset_table_id: string
  dataset_display_name: string | null
  benchmark_type: string | null
  subject_type: string
  mode: string
  rank_by: string
  metric_columns: LeaderboardMetricColumn[]
  items: LeaderboardEntry[]
  next_cursor: string | null
  has_more: boolean
  total: number
}

export interface LeaderboardDatasetItem {
  dataset_table_id: string
  display_name: string | null
  benchmark_type: string | null
  subject_count: number
  last_evaluated_at: number | null
}

export interface LeaderboardDatasetsResponse {
  items: LeaderboardDatasetItem[]
  next_cursor: string | null
  has_more: boolean
  total: number
}

export interface LeaderboardStatsResponse {
  subject_count: number
  metric_count: number
  median_overall: number | null
  last_evaluated_at: number | null
  metric_medians: Record<string, number>
}

interface PaginatedResponse<T> {
  data: T[]
  pagination: { total: number; limit: number; offset: number }
}

const { auth: authConfig } = config

/** Format FastAPI 422 validation errors (detail is array of { loc, msg, type }) into readable string. */
function formatAgentEvalsError(errorData: Record<string, unknown>, status: number): string {
  const detail = errorData?.detail
  const err = errorData?.error
  if (typeof err === 'string') return err
  if (Array.isArray(detail) && detail.length > 0) {
    const parts = detail.map((d: { loc?: unknown[]; msg?: string }) => {
      const loc = Array.isArray(d?.loc) ? d.loc.filter((x) => x !== 'body').join('.') : ''
      const msg = typeof d?.msg === 'string' ? d.msg : String(d?.msg ?? 'Validation error')
      return loc ? `${loc}: ${msg}` : msg
    })
    return parts.join('; ')
  }
  if (detail && typeof (detail as { message?: string }).message === 'string') {
    return (detail as { message: string }).message
  }
  if (typeof detail === 'string') return detail
  return `Failed to process agent evaluation request (Status: ${status}). Please try again later.`
}

async function agentEvalsRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const baseUrl = config.api.aiservicesUrl
  const url = `${baseUrl}/v1${endpoint}`

  let token: string | null = null
  try {
    token = localStorage.getItem(authConfig.tokenKey)
  } catch {
    // Continue without token
  }

  let tenantId: string | null = null
  try {
    tenantId = localStorage.getItem('tagp-tenant-id')
  } catch {
    // Ignore
  }

  const fetchConfig: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...(tenantId && { 'X-Tenant-ID': tenantId }),
      ...(options.headers || {}),
    },
    ...options,
  }

  const response = await agentEvalsFetch(url, fetchConfig)

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    const errorMessage = formatAgentEvalsError(errorData, response.status)
    const apiErr = new ApiError(errorMessage, response.status, errorData)
    maybeCaptureApiError(url, apiErr)
    throw apiErr
  }

  if (response.status === 204) {
    return undefined as T
  }

  const text = await response.text()
  if (!text || text.trim() === '') {
    return undefined as T
  }

  return JSON.parse(text) as T
}

async function withOAuthRefreshForLongRuns<T extends { oauth_refresh_token?: string | null }>(
  data: T,
): Promise<T> {
  const existing = (data.oauth_refresh_token || '').trim()
  if (existing) return data
  const rt = await resolveOAuthRefreshTokenForLongRuns()
  return rt ? { ...data, oauth_refresh_token: rt } : data
}

export const agentEvalsApi = {
  // Sessions
  createSession: async (data: CreateSessionPayload, options?: { signal?: AbortSignal }) => {
    const merged = await withOAuthRefreshForLongRuns(data)
    return agentEvalsRequest<EvalSession>(`/sessions`, {
      method: 'POST',
      body: JSON.stringify(merged),
      signal: options?.signal,
    })
  },

  listSessions: (params?: {
    agent_id?: string
    project_id?: string
    subject_type?: 'agent' | 'workflow'
    status?: string
    eval_framework?: string
    score_band?: string
    date_from?: string
    date_to?: string
    sort?: string
    order?: string
    limit?: number
    offset?: number
    include_summary_metrics?: boolean
    include_created_by_name?: boolean
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<PaginatedResponse<EvalSession>>(`/sessions${query}`, { signal })
  },

  /**
   * List-all sessions for dropdowns / multiselect pickers (capped server-side).
   * Prefer listSessions with limit/offset for DataTables.
   * Pass include_summary_metrics: false and include_created_by_name: false when
   * the picker only needs id / session_name / created_at.
   */
  listSessionsUnpaginated: (params?: {
    agent_id?: string
    project_id?: string
    subject_type?: 'agent' | 'workflow'
    status?: string
    eval_framework?: string
    score_band?: string
    date_from?: string
    date_to?: string
    sort?: string
    order?: string
    include_summary_metrics?: boolean
    include_created_by_name?: boolean
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString({ ...queryParams, unpaginated: true })
    return agentEvalsRequest<PaginatedResponse<EvalSession>>(`/sessions${query}`, { signal })
  },

  getSession: (sessionId: string, options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<EvalSession>(`/sessions/${sessionId}`, { signal: options?.signal }),

  getSessionRows: (
    sessionId: string,
    params?: {
      metric_filter?: string
      /** Metric key to threshold rows on (e.g. "deepeval::answer_correctness"). Required with score_lt/score_gte. */
      score_metric_key?: string
      /** Keep only rows whose score_metric_key score is < this value. */
      score_lt?: number
      /** Keep only rows whose score_metric_key score is >= this value. */
      score_gte?: number
      limit?: number
      offset?: number
      signal?: AbortSignal
    }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<PaginatedResponse<EvalRowResult>>(
      `/sessions/${sessionId}/rows${query}`,
      { signal }
    )
  },

  /**
   * Fetch all rows for a session without pagination (single request).
   * Use for session rows table and dropdowns that need the full row list.
   */
  getSessionRowsUnpaginated: (
    sessionId: string,
    params?: {
      metric_filter?: string
      /** Metric key to threshold rows on (e.g. "deepeval::answer_correctness"). Required with score_lt/score_gte. */
      score_metric_key?: string
      /** Keep only rows whose score_metric_key score is < this value. */
      score_lt?: number
      /** Keep only rows whose score_metric_key score is >= this value. */
      score_gte?: number
      signal?: AbortSignal
    }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString({ ...queryParams, unpaginated: true })
    return agentEvalsRequest<PaginatedResponse<EvalRowResult>>(
      `/sessions/${sessionId}/rows${query}`,
      { signal }
    )
  },

  /** Histogram distribution + mean, std_dev for a metric's per-query scores in a session. */
  getSessionMetricDistribution: (
    sessionId: string,
    params?: { metric_key: string; max_rows?: number; signal?: AbortSignal }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<ScoreDistributionResponse>(
      `/sessions/${sessionId}/metrics/distribution${query}`,
      { signal }
    )
  },

  cancelSession: (sessionId: string) =>
    agentEvalsRequest<{ status: string; session_id: string }>(`/sessions/${sessionId}/cancel`, {
      method: 'POST',
    }),

  retrySession: async (sessionId: string) => {
    const rt = await resolveOAuthRefreshTokenForLongRuns()
    return agentEvalsRequest<EvalSession>(`/sessions/${sessionId}/retry`, {
      method: 'POST',
      body: rt ? JSON.stringify({ oauth_refresh_token: rt }) : JSON.stringify({}),
    })
  },

  resumeSession: async (sessionId: string) => {
    const rt = await resolveOAuthRefreshTokenForLongRuns()
    return agentEvalsRequest<EvalSession>(`/sessions/${sessionId}/resume`, {
      method: 'POST',
      body: rt ? JSON.stringify({ oauth_refresh_token: rt }) : JSON.stringify({}),
    })
  },

  deleteSession: (sessionId: string) =>
    agentEvalsRequest<void>(`/sessions/${sessionId}`, { method: 'DELETE' }),

  // Metrics
  /** Summary stats for Agent Evals KPI cards. */
  agentEvalSummaryStats: (params?: {
    project_id?: string
    agent_id?: string
    session_id?: string
    subject_type?: 'agent' | 'workflow'
    metric_key?: string
    score_band?: string
    status?: string
    framework?: string
    date_from?: string
    date_to?: string
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<AgentEvalsSummary>(`/agents/summary/stats${query}`, { signal })
  },

  /** Paginated list: one aggregated entry per agent (latest metrics + session). Supports metric_keys, search. */
  agentPortfolioSummary: (params?: {
    project_id?: string
    subject_type?: 'agent' | 'workflow'
    metric_key?: string
    metric_keys?: string[]
    search?: string
    score_band?: string
    status?: string
    framework?: string
    date_from?: string
    date_to?: string
    limit?: number
    offset?: number
    signal?: AbortSignal
  }) => {
    const { signal, ...rest } = params || {}
    const searchParams = new URLSearchParams()
    Object.entries(rest).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      if (key === 'metric_keys' && Array.isArray(value)) {
        value.forEach((v) => searchParams.append('metric_keys', v))
      } else {
        searchParams.append(key, String(value))
      }
    })
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return agentEvalsRequest<PaginatedResponse<AgentPortfolioEntry>>(`/agents/summary${query}`, {
      signal,
    })
  },

  agentTrend: (
    agentId: string,
    params?: {
      metric_key?: string
      version?: string
      session_id?: string
      date_from?: string
      date_to?: string
      limit?: number
      offset?: number
      signal?: AbortSignal
    }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<PaginatedResponse<AgentSummaryItem>>(
      `/agents/${agentId}/metrics${query}`,
      { signal }
    )
  },

  agentTrendAll: (
    agentId: string,
    params?: {
      metric_key?: string
      version?: string
      session_id?: string[]
      date_from?: string
      date_to?: string
      signal?: AbortSignal
    }
  ) => {
    const { signal, session_id, ...queryParams } = params || {}
    const searchParams = new URLSearchParams()
    Object.entries(queryParams).forEach(([key, value]) => {
      if (value === undefined || value === null) return
      searchParams.append(key, String(value))
    })
    if (Array.isArray(session_id)) {
      session_id.filter(Boolean).forEach((sid) => searchParams.append('session_id', sid))
    }
    const query = searchParams.toString() ? `?${searchParams.toString()}` : ''
    return agentEvalsRequest<AgentSummaryItem[]>(`/agents/${agentId}/metrics/all${query}`, {
      signal,
    })
  },

  /** List a workflow's final-output tables for use as eval datasets. */
  listWorkflowOutputTables: (workflowId: string, options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<WorkflowOutputTablesResponse>(
      `/workflows/${workflowId}/output-tables`,
      { signal: options?.signal }
    ),

  metricsCatalog: (options?: { signal?: AbortSignal; project_id?: string }) => {
    const params = new URLSearchParams()
    if (options?.project_id) params.set('project_id', options.project_id)
    const query = params.toString() ? `?${params.toString()}` : ''
    return agentEvalsRequest<MetricCatalogResponse>(`/metrics/catalog${query}`, {
      signal: options?.signal,
    })
  },

  metricsGlossary: (options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<MetricGlossaryResponse>('/metrics/glossary', {
      signal: options?.signal,
    }),

  // Templates
  generateTemplate: (data: GenerateTemplatePayload, options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<GenerateTemplateResponse>(`/templates/generate`, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  createTemplate: (data: CreateTemplatePayload, options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<EvalTemplate>(`/templates`, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  batchCreateTemplates: (data: BatchCreateTemplatesPayload, options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<EvalTemplate[]>(`/templates/batch`, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  listTemplates: (params?: {
    project_id?: string
    search?: string
    limit?: number
    offset?: number
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<PaginatedResponse<EvalTemplate>>(`/templates${query}`, { signal })
  },

  getTemplate: (templateId: string, options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<EvalTemplate>(`/templates/${templateId}`, { signal: options?.signal }),

  updateTemplate: (
    templateId: string,
    data: UpdateTemplatePayload,
    options?: { signal?: AbortSignal }
  ) =>
    agentEvalsRequest<EvalTemplate>(`/templates/${templateId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  deleteTemplate: (templateId: string) =>
    agentEvalsRequest<void>(`/templates/${templateId}`, { method: 'DELETE' }),

  bulkDeleteTemplates: (templateIds: string[], options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<BulkTemplateDeleteResponse>(`/templates/bulk-delete`, {
      method: 'POST',
      body: JSON.stringify({ template_ids: templateIds }),
      signal: options?.signal,
    }),

  exportTemplates: (templateIds: string[], options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<ExportTemplatesResponse>(`/templates/export`, {
      method: 'POST',
      body: JSON.stringify({ template_ids: templateIds }),
      signal: options?.signal,
    }),

  importTemplates: (data: ImportTemplatesPayload, options?: { signal?: AbortSignal }) =>
    agentEvalsRequest<ImportTemplatesResponse>(`/templates/import`, {
      method: 'POST',
      body: JSON.stringify(data),
      signal: options?.signal,
    }),

  listLeaderboardDatasets: (
    params?: {
      subject_type?: 'agent' | 'workflow'
      benchmark_type?: string
      search?: string
      cursor?: string
      limit?: number
      signal?: AbortSignal
    }
  ) => {
    const { signal, ...queryParams } = params || {}
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<LeaderboardDatasetsResponse>(`/leaderboard/datasets${query}`, { signal })
  },

  getLeaderboard: (params: {
    dataset_table_id: string
    subject_type?: 'agent' | 'workflow'
    mode?: 'best' | 'latest'
    rank_by?: string
    search?: string
    cursor?: string
    limit?: number
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<LeaderboardResponse>(`/leaderboard${query}`, { signal })
  },

  getLeaderboardStats: (params: {
    dataset_table_id: string
    subject_type?: 'agent' | 'workflow'
    mode?: 'best' | 'latest'
    signal?: AbortSignal
  }) => {
    const { signal, ...queryParams } = params
    const query = buildQueryString(queryParams)
    return agentEvalsRequest<LeaderboardStatsResponse>(`/leaderboard/stats${query}`, { signal })
  },
}
